<template>
  <div>
    <b-form-group
      class="mt-1"
      label-for="boolInput"
    >
      <label
        v-if="type != 'statement'"
        v-html="name"
      />
  
      <div class="d-flex justify-content-start align-items-center">
        <div>
          <!-- Boolean -->
          <div v-if="type === 'bool'" class="d-flex justify-content-start align-items-center">
            <b-form-radio-group
              v-model="answer"
              :options="options"
              @change="answerChanged"
            />
          </div>
      
          <!-- Rating with Tooltips -->
          <div v-if="type === 'rating'" class="d-flex justify-content-start align-items-center">
            <div class="rating-container position-relative">
              <b-form-rating
                v-model="answer"
                size="lg"
                inline="inline"
                no-border="no-border"
                variant="warning"
                @change="answerChanged"
                @mouseover.native="handleStarHover"
                @mouseleave.native="hideTooltip"
              />

              <!-- Tooltip personalizado -->
              <div
                v-if="showTooltip"
                class="custom-tooltip"
                :style="tooltipStyle"
              >
                {{ tooltipText }}
              </div>
            </div>

            <InformationButton class="mr-1"/>

            <b-button
              class="mr-1"
              size="sm"
              variant="primary"
              :disabled="ratingNA === 'N/A'"
              @click="setRatingNA"
            >
              N/A
            </b-button>

            <strong v-show="answer || ratingNA" :style="{ fontSize: '1.15rem' }" class="mr-1">
              {{ answer ? answer : ratingNA }}
            </strong>      
          </div>
      
          <!-- Text -->
          <b-form-textarea
            v-if="type === 'text'"
            v-model="answer"
            rows="3"
            @change="answerChanged"
          />
      
          <!-- Statement -->
          <div v-if="type === 'statement'">
            <b-card
              v-html="showSeeMoreButton && showShortStatement ? shortStatement : name"
              class="mb-0"
            />
            <b-button
              v-if="showSeeMoreButton"
              size="sm"
              :variant="showShortStatement ? 'info' : 'warning'"
              @click="showShortStatement = !showShortStatement"
            >
              {{ showShortStatement ? $t('message.see_more') : $t('message.see_less') }}
            </b-button>
          </div>
        </div>

        <!-- Buttons -->
        <div v-if="type === 'bool' || type === 'rating'" class="d-flex justify-content-start align-items-center">      
          <!-- Document Repository -->
          <document-repository
            :documents="evidenceFiles"
            :keys="behaviour.evidenceKey || []"
            :read-only="false"
            :allowDeleteKeys="true"
            type="evidence"
            size="sm"
            uploadVariant="warning"
            badgeVariant="primary"
            buttonExtraText="evidences"
            @add="handleAddEvidences"
            @delete="handleDeleteEvidences"
          />
          <small v-if="requiresEvidence && !(evidenceFiles && evidenceFiles.length) && !(behaviour.evidenceKey && behaviour.evidenceKey.length)", class="ml-1 text-danger">
            {{ $t('message.upload_evidence_required') }}
          </small>

          <!-- Add improvement -->
          <b-button
            v-if="allowAddImprovement"
            class="ml-1"
            size="sm"
            :variant="behaviourHasImprovement ? 'warning' : 'info'"
            @click="isImprovementHandlerSidebarActive = true"
          >
            {{ behaviourHasImprovement ? $t('update_improvement') : $t('add_improvement') }}
          </b-button>
        </div>
      </div>

      <!-- Default text field for comments -->
      <b-form-input
        v-model="comment"
        class="mt-1"
        :placeholder="$t('placeholder.optionalComment')"
        @change="commentChanged"
      />
    </b-form-group>
  
    <!-- <commitment-handler-sidebar
      v-model="isCommitmentHandlerSidebarActive"
      :commitment="commitment"
      :behaviourHasCommitment="behaviourHasCommitment"
      :clear-commitment-data="clearCommitmentData"
      handlerId="commitment"
      @update-commitment="updateCommitment"
      @remove-commitment="removeCommitment"
    /> -->

    <todo-task-handler-sidebar
      v-model="isImprovementHandlerSidebarActive"
      :task="improvement"
      :behaviourHasImprovement="behaviourHasImprovement"
      :clear-task-data="clearImprovementData"
      @add-task="updateImprovement"
      @update-task="updateImprovement"
      @remove-task="removeImprovement"
    />
  </div>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import { BFormGroup, BFormRadioGroup, BFormRating, BFormTextarea, BButton } from 'bootstrap-vue'
import i18n from '@/libs/i18n'
import TodoTaskHandlerSidebar from '@/views/apps/todo/TodoTaskHandlerSidebar.vue'
import UploadFileButton from '@/views/habit/confirmation/shared/UploadFileButton.vue'
import { ratingLabels } from '@/constants'
import InformationButton from '@/views/components/Shared/Buttons/InformationButton.vue'
import useCommon from '@/views/organization/useCommon'
import { useRouter } from '@core/utils/utils'
import DocumentRepository from '@/views/habit/DocumentRepository'

export default {
  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormRating,
    BFormTextarea,
    BButton,
    TodoTaskHandlerSidebar,
    UploadFileButton,
    InformationButton,
    DocumentRepository,
  },

  props: {
    behaviour: {
      type: Object,
      required: true,
    },
    allowAddImprovement: {
      type: Boolean,
      default: false,
    },
    clientId: {
      type: String,
      required: true,
    },
    activityName: {
      type: String,
      required: true,
    },
    excludeNAOption: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      name: this.behaviour.name,
      type: this.behaviour.type,
      // answer: this.behaviour.answer,
      comment: this.behaviour.comment,
      requiresEvidence: this.behaviour.requiresEvidence,
      showTooltip: false,
      tooltipText: '',
      tooltipStyle: {},
    }
  },

  computed: {
    shortStatement() {
      if (this.type === 'statement') {
        return this.name.slice(0, 100) + "..."
      }
    },
    showSeeMoreButton() {
      return this.type === 'statement' && this.name?.length > 100 && !this.name?.startsWith("<p><a href")
    }
  },

  methods: {
    answerChanged(answerValue) {
      if (this.type === 'rating') {
        if (!answerValue) return
        this.ratingNA = ''
      }
      this.$emit('answered', answerValue)
    },
    commentChanged(comment) {
      this.$emit('commented', comment)
    },
    handleStarHover(event) {
      // Encontrar el elemento estrella más cercano
      const star = event.target.closest('.b-rating-star')
      if (!star) return

      // Obtener el índice de la estrella basado en su posición
      const stars = star.parentElement.querySelectorAll('.b-rating-star')
      const starIndex = Array.from(stars).indexOf(star)

      // Configurar el tooltip
      this.tooltipText = ratingLabels.value[starIndex] || ''
      
      if (this.tooltipText) {
        const ratingContainer = this.$el.querySelector('.rating-container')
        const containerRect = ratingContainer.getBoundingClientRect()
        const starRect = star.getBoundingClientRect()
        
        // Calcular posición relativa al contenedor
        const relativeLeft = starRect.left - containerRect.left + (starRect.width / 2)
        
        this.tooltipStyle = {
          position: 'absolute',
          top: '-30px',
          left: `${relativeLeft}px`,
          transform: 'translateX(-50%)',
          zIndex: 1000
        }
        this.showTooltip = true
      }
    },
    hideTooltip() {
      this.showTooltip = false
    }
  },

  setup(props, { emit }) {
    const answer = ref(props.behaviour.answer || null)

    // answer solo se inicializa acá arriba a partir de props.behaviour.answer; no hay reactividad
    // automática después de eso. Sin este watch, una actualización externa del behaviour (ej.
    // "Recargar evaluaciones" trayendo una respuesta ya cargada por el responsable) no se refleja
    // en pantalla aunque el dato ya esté actualizado en memoria.
    watch(() => props.behaviour.answer, newAnswer => {
      answer.value = newAnswer || null
    })

    const ratingNA = ref('')
    const showShortStatement = ref(true)
    const today = new Date()
    const comment = ref('')
    const { openDocument } = useCommon()
    const { router } = useRouter()
    const evidenceFiles = ref([])

    const options = [
      { text: i18n.t('message.yes'), value: 'true' },
      { text: 'No', value: 'false' },
      { text: 'N/A', value: 'unknown' },
    ]

    if (props.excludeNAOption) options.pop()

    const setRatingNA = () => {
      answer.value = null
      ratingNA.value = 'N/A'
      emit('answered', ratingNA.value)
    }

    // Improvement sidebar
    const isImprovementHandlerSidebarActive = ref(false)
    const behaviourHasImprovement = ref(false)

    const blankImprovement = {
      client_id: { link: props.clientId },
      note: '',
      assignee: '',
      createdAt: today,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      activity: props.activityName,
      behaviour: {
        name: props.behaviour.name,
        type: props.behaviour.type,
      },
      // objective: '',
    }

    const improvement = ref(JSON.parse(JSON.stringify(blankImprovement)))

    const clearImprovementData = () => {
      // improvement.value = JSON.parse(JSON.stringify(blankImprovement))
    }

    const updateImprovement = (improvementData) => {
      // Wait untill the sidebar closing transition is finished
      setTimeout(() => {
        behaviourHasImprovement.value = true
        emit('improvementModified', improvementData)
      }, 100)
    }

    const removeImprovement = () => {
      isImprovementHandlerSidebarActive.value = false

      // Wait untill the sidebar closing transition is finished
      setTimeout(() => {
        behaviourHasImprovement.value = false
        improvement.value = JSON.parse(JSON.stringify(blankImprovement))
        emit('improvementRemoved')
      }, 100)
    }

    const openCase = (id) => {
      const routeData = router.resolve({ name: 'organization-case-edit', params: { id } });
      window.open(routeData.href, '_blank');
    }

    const handleAddEvidences = (newDocs) => {
      evidenceFiles.value.push(...newDocs)
      emit('fileToUpload', evidenceFiles.value)
    }

    const handleDeleteEvidences = (index, isUploaded, key) => {
      if (isUploaded) emit('deleteKey', key)
      else {
        evidenceFiles.value.splice(index, 1)
        emit('fileToUpload', evidenceFiles.value)
      }
    }

    return {
      answer,
      ratingNA,
      options,
      setRatingNA,
      showShortStatement,
      isImprovementHandlerSidebarActive,
      improvement,
      clearImprovementData,
      updateImprovement,
      removeImprovement,
      behaviourHasImprovement,
      comment,
      openDocument,
      openCase,
      evidenceFiles,
      handleAddEvidences,
      handleDeleteEvidences,
    }
  },
}
</script>

<style scoped>
.rating-container {
  position: relative;
  display: inline-block;
}

.custom-tooltip {
  background-color: #ff8c00;
  color: rgb(255, 255, 255);
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: absolute;
  pointer-events: none;
}

.custom-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #ff8c00;
}

/* Asegurar que las estrellas sean interactivas para el hover */
.rating-container >>> .b-rating-star {
  cursor: pointer;
}

/* Asegurar que el rating tenga el posicionamiento correcto */
.rating-container >>> .b-rating {
  position: relative;
}
</style>